import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Star, CheckCircle, Shield, Award, Briefcase, Zap, Globe, Clock, ArrowRight, Languages, MessageCircle, ExternalLink, Loader2 } from 'lucide-react';
import { ServiceCard } from '../layout/ServiceCard';
import { QuoteModal } from '../interactions/QuoteModal';
import { ServicesService, ServiceStatus } from '../../services/servicesService';
import { CATEGORIES } from '../../constants/serviceConstants';

export const ProviderProfile = ({ 
  provider, 
  providerServices: initialServices,
  onClose,
  onBookService
}) => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [services, setServices] = useState(initialServices || []);
  const [isLoading, setIsLoading] = useState(!initialServices || initialServices.length === 0);

  // Load services from Firestore if not provided
  useEffect(() => {
    const loadServices = async () => {
      if (initialServices && initialServices.length > 0) {
        setServices(initialServices);
        setIsLoading(false);
        return;
      }
      
      try {
        const providerServices = await ServicesService.getByProviderId(provider.id, ServiceStatus.PUBLISHED);
        setServices(providerServices);
      } catch (error) {
        console.error('Error loading provider services:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadServices();
  }, [provider.id, initialServices]);

  // Get provider's categories from their services
  const providerCategories = [...new Set(services.map(s => {
    const cat = CATEGORIES.find(c => c.id === s.categoryId);
    return cat?.name || s.category;
  }).filter(Boolean))];

  const handleQuoteSubmit = (data) => {
    console.log('Quote Request:', data);
    alert(`Quote request sent to ${provider.name}!\n\nThey will review your project details and get back to you shortly.`);
    setIsQuoteModalOpen(false);
  };

  const formatJoinDate = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      const date = dateString.toDate ? dateString.toDate() : new Date(dateString);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-5xl border border-slate-100 flex flex-col max-h-[90vh]">
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-20 bg-black/20 hover:bg-black/40 backdrop-blur-md p-2 rounded-full text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Scrollable Content */}
          <div className="overflow-y-auto custom-scrollbar">
            
            {/* Banner */}
            <div className="h-48 md:h-64 w-full bg-slate-200 relative">
              {provider.coverUrl ? (
                <img src={provider.coverUrl} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="px-6 pb-10 sm:px-10">
              
              {/* Header */}
              <div className="relative -mt-20 mb-8 flex flex-col md:flex-row items-start md:items-end gap-6">
                
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {provider.avatarUrl ? (
                    <img 
                      src={provider.avatarUrl} 
                      alt={provider.name} 
                      className="h-32 w-32 md:h-40 md:w-40 rounded-2xl ring-4 ring-white shadow-lg bg-white object-cover"
                    />
                  ) : (
                    <div className="h-32 w-32 md:h-40 md:w-40 rounded-2xl ring-4 ring-white shadow-lg bg-indigo-100 flex items-center justify-center">
                      <span className="text-5xl font-bold text-indigo-600">{provider.name?.charAt(0) || '?'}</span>
                    </div>
                  )}
                  {provider.isVerified && (
                     <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white p-1.5 rounded-full ring-2 ring-white" title="Verified Provider">
                       <CheckCircle className="w-4 h-4" />
                     </div>
                  )}
                </div>

                {/* Name & Details */}
                <div className="flex-1 text-left mb-2 w-full">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h2 className="text-3xl font-bold text-slate-900 tracking-tight">{provider.name}</h2>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600 mt-2">
                        {provider.location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-slate-400" /> 
                            {provider.location}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-slate-400" /> 
                          Joined {formatJoinDate(provider.joinedDate || provider.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 mt-2 md:mt-0">
                      <button 
                        onClick={() => setIsQuoteModalOpen(true)}
                        className="px-5 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-lg hover:bg-slate-800 transition-colors shadow-sm flex items-center gap-2"
                      >
                        <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                        Quick Quote
                      </button>
                      <button 
                        onClick={() => alert(`Starting chat with ${provider.name}...\n\nDirect messaging feature coming soon!`)}
                        className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                      >
                        Contact Me
                      </button>
                      
                      {provider.portfolioUrl && (
                        <a 
                          href={provider.portfolioUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2"
                        >
                          <Globe className="w-4 h-4 text-slate-500" />
                          Portfolio
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 text-yellow-600 rounded-lg">
                    <Star className="w-5 h-5 fill-current" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">{provider.rating || 0}</div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Rating</div>
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">{provider.jobsCompleted || 0}+</div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Jobs Done</div>
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                    <Award className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">{provider.reviewCount || 0}</div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Reviews</div>
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                   <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">{provider.isVerified ? 'Verified' : 'Active'}</div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                
                {/* Left Column */}
                <div className="lg:col-span-1 space-y-8">
                  {provider.bio && (
                    <section>
                      <h3 className="text-lg font-bold text-slate-900 mb-3">About</h3>
                      <p className="text-slate-600 leading-relaxed text-sm">{provider.bio}</p>
                    </section>
                  )}
                  
                  {/* Professional Details */}
                  <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-5">
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-2">Professional Details</h3>
                      
                      {provider.yearsExperience && (
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500">
                                <Clock className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">{provider.yearsExperience} Years</p>
                                <p className="text-xs text-slate-500">Experience</p>
                            </div>
                        </div>
                      )}

                      {provider.responseTime && (
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500">
                                <MessageCircle className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">{provider.responseTime}</p>
                                <p className="text-xs text-slate-500">Avg. Response Time</p>
                            </div>
                        </div>
                      )}

                      {provider.certifications && provider.certifications.length > 0 && (
                         <div className="flex items-start gap-3">
                            <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 mt-1">
                                <Award className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900 mb-1">Certifications</p>
                                <ul className="space-y-1.5">
                                    {provider.certifications.map((cert, idx) => (
                                        <li key={idx} className="text-xs text-slate-600 flex items-start gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>
                                            {cert}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                      )}

                       {provider.languages && provider.languages.length > 0 && (
                         <div className="flex items-start gap-3">
                            <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 mt-1">
                                <Languages className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900 mb-1">Languages</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {provider.languages.map((lang, idx) => (
                                        <span key={idx} className="text-xs bg-white text-slate-600 px-2 py-1 rounded border border-slate-200">
                                            {lang}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                      )}

                      {provider.portfolioUrl && (
                         <div className="pt-2 border-t border-slate-200 mt-2">
                             <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 mt-2">Portfolio</p>
                             <a 
                               href={provider.portfolioUrl} 
                               target="_blank" 
                               rel="noreferrer" 
                               className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all group"
                             >
                                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                    <Globe className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">Visit Website</p>
                                    <p className="text-xs text-slate-500 truncate">{provider.portfolioUrl.replace(/^https?:\/\//, '')}</p>
                                </div>
                                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" />
                            </a>
                        </div>
                      )}
                  </section>

                  {/* Categories */}
                  {providerCategories.length > 0 && (
                    <section>
                      <h3 className="text-lg font-bold text-slate-900 mb-3">Categories</h3>
                      <div className="flex flex-wrap gap-2">
                        {providerCategories.map((cat, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 shadow-sm flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                            {cat}
                          </span>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Badges */}
                  {provider.badges && provider.badges.length > 0 && (
                    <section>
                       <h3 className="text-lg font-bold text-slate-900 mb-3">Badges & Skills</h3>
                       <div className="flex flex-wrap gap-2">
                         {provider.badges.map(badge => (
                           <span key={badge} className="px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full text-xs font-semibold text-indigo-700">
                             {badge}
                           </span>
                         ))}
                       </div>
                    </section>
                  )}
                </div>
                
                {/* Right Column: Services */}
                <div className="lg:col-span-2">
                  <div className="flex items-center justify-between mb-6">
                     <h3 className="text-xl font-bold text-slate-900">
                       Available Services ({services.length})
                     </h3>
                  </div>
                  
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                  ) : services.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {services.map(service => (
                        <ServiceCard 
                          key={service.id} 
                          service={service} 
                          provider={provider}
                          onBook={onBookService}
                          onProviderClick={() => {}} 
                          compact={false}
                          hideProvider={true}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-2xl p-12 text-center border border-dashed border-slate-200">
                      <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h4 className="text-lg font-bold text-slate-900 mb-2">No Services Yet</h4>
                      <p className="text-slate-500 text-sm">This provider hasn't published any services yet.</p>
                    </div>
                  )}

                  {/* Portfolio Items */}
                  {provider.portfolioItems && provider.portfolioItems.length > 0 && (
                    <div className="mt-12 border-t border-slate-100 pt-10">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-slate-900">Featured Work</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {provider.portfolioItems.map((item) => (
                          <a 
                            key={item.id} 
                            href={item.linkUrl} 
                            target="_blank" 
                            rel="noreferrer"
                            className="group block bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all duration-300 relative"
                          >
                            <div className="h-48 overflow-hidden relative">
                               <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                               <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                               <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                 <ExternalLink className="w-4 h-4 text-slate-900" />
                               </div>
                               {item.category && (
                                 <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
                                   <span className="text-xs font-medium text-white">{item.category}</span>
                                 </div>
                               )}
                            </div>
                            <div className="p-4">
                              <h4 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition-colors">{item.title}</h4>
                              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1 group-hover:text-indigo-500">
                                View Project <ArrowRight className="w-3 h-3" />
                              </p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuoteModal 
        provider={provider}
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        onSubmit={handleQuoteSubmit}
      />
    </div>
  );
};